import type NumberUnit from '@exodus/currency';
import { ExchangeForm } from '@exodus/exchange-ui';
import type { ComponentProps } from '@exodus/exchange-ui/lib/form/components/error';
import { useValidationContext } from '@exodus/exchange-ui/lib/form/context/validation';
import { formatAssetAmount } from '@exodus/formatting/lib/asset';
import type { Asset } from '@passkeys/assets';
import Callout from '@passkeys/ui/callout';
import { useSelector } from 'react-redux';

import selectors from '@/ui/flux/selectors';
import { createNavigateStateOptions, useNavigate } from '@/ui/utils/navigation';

type ErrorProps<T = Record<string, never>> = { className?: string; payload: T };

const NotEnoughBalanceError = ({ className }: ErrorProps) => {
  const text = 'You have insufficient funds for this action.';

  return (
    <Callout className={className} type="error">
      {text}
    </Callout>
  );
};

type FromAmountPayload = {
  asset: Asset;
  limits: { min: NumberUnit; max: NumberUnit };
};

const LowFromAmountError = ({
  className,
  payload,
}: ErrorProps<FromAmountPayload>) => {
  const { limits, asset } = payload;
  const text = `The amount is lower than the exchange minimum of ${formatAssetAmount(limits.min.toDefaultNumber())} ${asset.displayTicker}.`;

  return (
    <Callout className={className} type="error">
      {text}
    </Callout>
  );
};

const HighFromAmountError = ({
  className,
  payload,
}: ErrorProps<FromAmountPayload>) => {
  const { limits, asset } = payload;
  const text = `The amount is higher than the exchange maximum of ${formatAssetAmount(limits.max.toDefaultNumber())} ${asset.displayTicker}.`;

  return (
    <Callout className={className} type="error">
      {text}
    </Callout>
  );
};

const NotEnoughFeeBalance = ({
  className,
  payload,
}: ErrorProps<{ fromAsset: Asset }>) => {
  const { fromAsset } = payload;
  const { feeAsset } = fromAsset;

  const navigate = useNavigate();

  const isBuySupportedByAsset = useSelector(
    selectors.fiatRamp.isBuySupportedByAsset,
  );

  const text = `You have insufficient ${feeAsset.displayTicker} to pay for this transaction’s fee.`;

  const action = isBuySupportedByAsset(feeAsset.name) && {
    label: `Buy ${feeAsset.displayTicker}`,
    onClick: () => {
      navigate(
        `/buy`,
        createNavigateStateOptions({
          token: feeAsset?.name,
          referrer: 'swap',
        }),
      );
    },
  };

  return (
    <Callout className={className} action={action} type="error">
      {text}
    </Callout>
  );
};

const PairUnavailableError = ({ className }: ErrorProps) => {
  const text =
    'This pair is currently unavailable. Please try again later or select a different pair.';

  return (
    <Callout className={className} type="error">
      {text}
    </Callout>
  );
};

const HighPriceImpactError = ({
  className,
  payload,
}: ErrorProps<{ spreadPercentage: number }>) => {
  const { spreadPercentage } = payload;
  const text = `You'll receive a low amount. The price impact is ${spreadPercentage.toFixed(2)}%`;

  return (
    <Callout className={className} type="error">
      {text}
    </Callout>
  );
};

const EosAccountCreationInProgress = ({ className }: ErrorProps) => {
  return (
    <Callout className={className} type="error">
      Your EOS account is being created. Once your EOS arrives, you can start
      another swap.
    </Callout>
  );
};

const REASON = {
  NOT_ENOUGH_BALANCE: NotEnoughBalanceError,
  LOW_FROM_AMOUNT: LowFromAmountError,
  HIGH_FROM_AMOUNT: HighFromAmountError,
  NOT_ENOUGH_FEE_BALANCE: NotEnoughFeeBalance,
  PAIR_UNAVAILABLE: PairUnavailableError,
  HIGH_PRICE_IMPACT: HighPriceImpactError,
  EOS_ACCOUNT_CREATION_IN_PROGRESS: EosAccountCreationInProgress,
};

function ErrorBanner({ reason, payload, className }: ComponentProps) {
  const Component = REASON[reason];

  if (!Component) {
    return null;
  }

  return <Component className={className} payload={payload} />;
}

function FormError() {
  const { currentError } = useValidationContext();

  if (currentError?.reason === 'PAIR_UNAVAILABLE') {
    return (
      <ErrorBanner
        type={currentError.type}
        reason="PAIR_UNAVAILABLE"
        payload={currentError.payload}
      />
    );
  }

  return <ExchangeForm.Error Component={ErrorBanner} />;
}

export default FormError;
